import type { ReactNode } from 'react';
import { useSettingsStore } from '../../stores/settingsStore';

const CODE_FONTS = [
  { label: 'System Default', value: 'ui-monospace, monospace' },
  { label: 'SF Mono', value: "'SF Mono', ui-monospace, monospace" },
  { label: 'JetBrains Mono', value: "'JetBrains Mono', monospace" },
  { label: 'Fira Code', value: "'Fira Code', monospace" },
  { label: 'Source Code Pro', value: "'Source Code Pro', monospace" },
];

const CODE_THEMES = [
  'github-dark',
  'github-light',
  'monokai',
  'nord',
  'dracula',
  'one-dark-pro',
  'vitesse-dark',
  'min-dark',
  'slack-dark',
] as const;

const DENSITY_OPTIONS = [
  { label: 'Compact', value: 'compact' as const },
  { label: 'Comfortable', value: 'comfortable' as const },
  { label: 'Spacious', value: 'spacious' as const },
];

const SELECT_CLASSES =
  'w-full appearance-none bg-card border border-input rounded-lg text-foreground text-sm px-3 py-2 focus:border-ring focus:ring-1 focus:ring-ring outline-none';

const TOGGLE_BASE_CLASSES = 'rounded-lg border border-input px-3 py-2 text-sm transition-colors';

export default function EditorTab() {
  const settings = useSettingsStore((s) => s.settings);
  const updateEditor = useSettingsStore((s) => s.updateEditor);
  const { fontSize, codeFontFamily, codeTheme, messageDensity, sendOnEnter } = settings.editor;

  return (
    <div className="space-y-8">
      <SettingSection title="Font Size" description="Base font size for chat messages.">
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={12}
            max={20}
            value={fontSize}
            onChange={(event) => updateEditor({ fontSize: Number(event.target.value) })}
            className="flex-1 accent-ring"
          />
          <span className="w-10 text-right font-mono text-sm text-foreground">{fontSize}px</span>
        </div>
      </SettingSection>

      <SettingSection
        title="Code Font"
        description="Font used in code blocks. Must be installed on your system."
      >
        <select
          value={codeFontFamily}
          onChange={(event) => updateEditor({ codeFontFamily: event.target.value })}
          className={SELECT_CLASSES}
        >
          {CODE_FONTS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </SettingSection>

      <SettingSection
        title="Code Syntax Theme"
        description="Syntax highlighting theme for code blocks."
      >
        <select
          value={codeTheme}
          onChange={(event) => updateEditor({ codeTheme: event.target.value })}
          className={SELECT_CLASSES}
        >
          {CODE_THEMES.map((theme) => (
            <option key={theme} value={theme}>
              {theme}
            </option>
          ))}
        </select>
      </SettingSection>

      <SettingSection
        title="Message Density"
        description="Controls spacing between chat messages."
      >
        <div className="flex gap-2">
          {DENSITY_OPTIONS.map((option) => {
            const isActive = messageDensity === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => updateEditor({ messageDensity: option.value })}
                className={`${TOGGLE_BASE_CLASSES} ${
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-muted'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </SettingSection>

      <SettingSection title="Send Message" description="Choose how to send messages.">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => updateEditor({ sendOnEnter: true })}
            className={`${TOGGLE_BASE_CLASSES} ${
              sendOnEnter
                ? 'bg-accent text-accent-foreground'
                : 'bg-secondary text-secondary-foreground hover:bg-muted'
            }`}
          >
            Enter to send
          </button>
          <button
            type="button"
            onClick={() => updateEditor({ sendOnEnter: false })}
            className={`${TOGGLE_BASE_CLASSES} ${
              !sendOnEnter
                ? 'bg-accent text-accent-foreground'
                : 'bg-secondary text-secondary-foreground hover:bg-muted'
            }`}
          >
            Ctrl+Enter to send
          </button>
        </div>
      </SettingSection>
    </div>
  );
}

function SettingSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-0.5 text-sm font-semibold text-foreground">{title}</h4>
      <p className="mb-3 text-xs text-muted-foreground">{description}</p>
      {children}
    </div>
  );
}
